import { useCallback, useState } from 'react';
import hotToast from 'react-hot-toast';

export interface SheetOption {
  value: string;
  label: string;
}

export const useExcelSheets = () => {
  const [sheetOptions, setSheetOptions] = useState<SheetOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSheetIndex, setSelectedSheetIndex] = useState<string>('');

  const readSheetsFromFile = useCallback(async (file: File | null) => {
    setSelectedFile(file);
    hotToast.dismiss();

    if (!file) {
      setSheetOptions([]);
      setSelectedSheetIndex('');
      return;
    }

    setIsLoading(true);
    try {
      const XLSX = await import('xlsx');
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array', bookSheets: true });

      const sheets: SheetOption[] = workbook.SheetNames.map((sheetName, index) => ({
        value: String(index),
        label: sheetName
      }));

      setSheetOptions(sheets);
      if (sheets.length > 0) {
        setSelectedSheetIndex(sheets[0].value);
      }
    } catch (error) {
      console.error('Error reading Excel file:', error);
      setSheetOptions([]);
      setSelectedSheetIndex('');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshSheets = useCallback(async () => {
    if (!selectedFile) return;
    await readSheetsFromFile(selectedFile);
  }, [selectedFile, readSheetsFromFile]);

  const clearSheets = useCallback(() => {
    setSheetOptions([]);
    setSelectedFile(null);
    setSelectedSheetIndex('');
  }, []);

  return {
    sheetOptions,
    isLoading,
    selectedFile,
    selectedSheetIndex,
    setSelectedSheetIndex,
    readSheetsFromFile,
    refreshSheets,
    clearSheets
  };
};
